import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import './Dashboard.css';
import { FaSpinner } from 'react-icons/fa';
import { ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Line, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import ActivityLogModal from './ActivityLogModal';
import { fetchSettledBatches, aggregateBatchesByMonth } from '../utils/authorizeBatches';

const Dashboard = () => {
  const [stats, setStats] = useState({ users: 0, campaigns: 0, takeovers: 0, creatives: 0, revenue: 0 });
  const [todo, setTodo] = useState({ takeoversNoStatus: 0, campaignsAwaitingApproval: 0, creativesPending: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [userName, setUserName] = useState('');
  const [userGoalTarget, setUserGoalTarget] = useState(1000);
  const [showGoalEdit, setShowGoalEdit] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [goalSaving, setGoalSaving] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [businessCount, setBusinessCount] = useState(0);
  const [personalCount, setPersonalCount] = useState(0);

  const dashboardSettingsRef = () => doc(db, 'admin_dashboard', 'config');
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchRevenue();
    if (auth.currentUser) setUserName(auth.currentUser.displayName || auth.currentUser.email);

    (async () => {
      try {
        const snap = await getDoc(dashboardSettingsRef());
        if (snap.exists()) {
          const raw = snap.data().userGrowthTarget;
          const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
          if (!isNaN(n) && n > 0) setUserGoalTarget(n);
        }
      } catch (err) {
        console.error('Error loading dashboard goal:', err);
      }
    })();
  }, []);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const sevenDaysAgo = dayjs().subtract(7, 'day').toDate();
      const [u, c, o, cr] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'campaigns')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'creatives'))
      ]);
      
      let biz = 0, pers = 0, newUsers = 0;
      u.forEach(d => {
        const data = d.data();
        data.isBusinessAccount ? biz++ : pers++;
        
        // Check if user joined in last 7 days
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : null);
        if (createdAt && createdAt >= sevenDaysAgo) {
          newUsers++;
        }
      });
      setBusinessCount(biz);
      setPersonalCount(pers);

      let cPending = 0, cActive = 0;
      c.forEach(d => {
        const status = d.data().status;
        if (status === 'Awaiting Approval') cPending++;
        if (status === 'Active' || status === 'Approved') cActive++;
      });
      
      let tAttention = 0;
      o.forEach(d => {
        const data = d.data();
        if (!data.status || data.status === 'Pending') tAttention++;
      });

      let crPending = 0;
      cr.forEach(d => d.data().moderationStatus === 'pending' && crPending++);

      setStats({ 
        users: u.size, 
        newUsersWeek: newUsers,
        campaigns: c.size, 
        activeCampaigns: cActive,
        takeovers: o.size, 
        creatives: cr.size, 
        revenue: 0 
      });
      setTodo({ campaignsAwaitingApproval: cPending, takeoversNoStatus: tAttention, creativesPending: crPending });
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchRevenue = async () => {
    setRevenueLoading(true);
    try {
      // Last 3 calendar months from Authorize.net settled batches (same source as Finances → Batches)
      const start = dayjs().subtract(2, 'month').startOf('month');
      const end = dayjs();
      const months = [];
      for (let i = 2; i >= 0; i--) {
        const m = dayjs().subtract(i, 'month');
        months.push({
          month: m.format('MMM'),
          monthKey: m.format('YYYY-MM'),
          charges: 0,
          refunds: 0,
          total: 0,
        });
      }

      const data = await fetchSettledBatches({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
      });

      if (data.success) {
        const byMonth = aggregateBatchesByMonth(data.batches);
        months.forEach((m) => {
          const s = byMonth[m.monthKey] || {};
          m.charges = +(s.chargeAmount || 0).toFixed(2);
          m.refunds = +(s.refundAmount || 0).toFixed(2);
          m.total = +(m.charges - m.refunds).toFixed(2);
        });
      }

      setMonthlyRevenue(months);

      const totalRev = months.reduce((sum, m) => sum + m.total, 0);
      setStats((prev) => ({ ...prev, revenue: totalRev }));
    } catch (err) {
      console.error('Error fetching revenue:', err);
    } finally {
      setRevenueLoading(false);
    }
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const userGoalPercent = Math.min(100, Math.round((stats.users / userGoalTarget) * 100));
  const usersToGo = Math.max(0, userGoalTarget - stats.users);

  const handleTodoClick = (type) => {
    if (type === 'campaigns') navigate('/campaigns');
    else if (type === 'takeovers') navigate('/takeovers');
    else if (type === 'creatives-pending') navigate('/creatives');
  };

  const persistUserGoal = async (val) => {
    setGoalSaving(true);
    try {
      await setDoc(
        dashboardSettingsRef(),
        { userGrowthTarget: val, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setUserGoalTarget(val);
      setShowGoalEdit(false);
    } catch (err) {
      console.error('Error saving dashboard goal:', err);
      window.alert('Could not save the goal. Please try again.');
    } finally {
      setGoalSaving(false);
    }
  };

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">{getGreeting()}, {userName || 'Admin'}</h1>
            <p className="f-subtitle mb-0">{dayjs().format('dddd, MMMM D, YYYY')}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" className="f-button" onClick={() => navigate('/analytics')}>
              View Analytics
            </button>
            <button type="button" className="f-button f-button-secondary" onClick={() => setShowActivityLog(true)}>
              Activity Log
            </button>
          </div>
        </div>

        <div className="f-card border-l-4 border-l-primary">
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">User Growth Target</div>
              <div className="text-2xl font-semibold text-slate-900">
                {statsLoading ? <FaSpinner className="fa-spin" /> : `${stats.users.toLocaleString()} / ${userGoalTarget.toLocaleString()}`}
              </div>
            </div>
            <button
              type="button"
              className="f-button f-button-secondary shrink-0"
              onClick={() => {
                setShowGoalEdit(true);
                setGoalInput(String(userGoalTarget));
              }}
            >
              Edit Goal
            </button>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
              style={{ width: `${userGoalPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500">
            <span>
              {statsLoading
                ? 'Loading...'
                : stats.users >= userGoalTarget
                  ? 'Goal achieved'
                  : `${usersToGo.toLocaleString()} more to reach goal`}
            </span>
            <span>{userGoalPercent}%</span>
          </div>
        </div>

        <div className="f-grid">
          <button
            type="button"
            className="f-card f-stat-card clickable w-full text-left"
            onClick={() => navigate('/users')}
          >
            <span className="f-stat-label">Total Users</span>
            <span className="f-stat-value">{statsLoading ? <FaSpinner className="fa-spin" /> : stats.users}</span>
            <div className="text-xs text-slate-500">
              {stats.newUsersWeek > 0 ? (
                <span className="font-semibold text-emerald-600">+{stats.newUsersWeek} this week</span>
              ) : (
                'No new users this week'
              )}
            </div>
          </button>
          <button
            type="button"
            className="f-card f-stat-card clickable w-full text-left"
            onClick={() => navigate('/campaigns')}
          >
            <span className="f-stat-label">Active Campaigns</span>
            <span className="f-stat-value">{statsLoading ? <FaSpinner className="fa-spin" /> : stats.activeCampaigns}</span>
            <div className="text-xs text-slate-500">{todo.campaignsAwaitingApproval} awaiting approval</div>
          </button>
          <button
            type="button"
            className="f-card f-stat-card clickable w-full text-left"
            onClick={() => navigate('/takeovers')}
          >
            <span className="f-stat-label">Takeovers</span>
            <span className="f-stat-value">{statsLoading ? <FaSpinner className="fa-spin" /> : stats.takeovers}</span>
            <div className="text-xs text-slate-500">{todo.takeoversNoStatus} require attention</div>
          </button>
          <button
            type="button"
            className="f-card f-stat-card clickable w-full text-left"
            onClick={() => navigate('/finances')}
          >
            <span className="f-stat-label">3-Month Revenue</span>
            <span className="f-stat-value text-emerald-600">
              {revenueLoading ? <FaSpinner className="fa-spin" /> : `$${stats.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            </span>
            <div className="text-xs text-slate-500">Authorize.net settled batches</div>
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="f-card lg:col-span-2">
            <h3 className="mb-5 text-base font-semibold text-slate-900">Last 3 Months Revenue</h3>
            <div className="h-[280px] md:h-[300px]">
              {revenueLoading ? (
                <div className="flex h-full items-center justify-center text-primary">
                  <FaSpinner className="fa-spin text-2xl" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    <Bar dataKey="charges" fill="#107c10" name="Charges" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="refunds" fill="#d13438" name="Refunds" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="total" stroke="#0b7fd4" strokeWidth={2} dot={{ r: 4 }} name="Net Revenue" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="f-card">
            <h3 className="mb-5 text-base font-semibold text-slate-900">Action Items</h3>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => handleTodoClick('takeovers')}
                className={`flex cursor-pointer items-center justify-between rounded-lg border border-transparent px-3 py-3 text-left transition hover:border-slate-200 ${
                  todo.takeoversNoStatus > 0 ? 'border-l-4 border-l-amber-400 bg-amber-50/90' : 'bg-slate-50'
                }`}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-slate-900">Process Takeovers</span>
                  <span className="text-xs text-slate-500">Pending status or payment</span>
                </div>
                <span className="text-lg font-bold text-primary">{todo.takeoversNoStatus}</span>
              </button>
              <button
                type="button"
                onClick={() => handleTodoClick('campaigns')}
                className={`flex cursor-pointer items-center justify-between rounded-lg border border-transparent px-3 py-3 text-left transition hover:border-slate-200 ${
                  todo.campaignsAwaitingApproval > 0 ? 'border-l-4 border-l-amber-400 bg-amber-50/90' : 'bg-slate-50'
                }`}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-slate-900">Approve Campaigns</span>
                  <span className="text-xs text-slate-500">New submissions waiting</span>
                </div>
                <span className="text-lg font-bold text-primary">{todo.campaignsAwaitingApproval}</span>
              </button>
              <button
                type="button"
                onClick={() => handleTodoClick('creatives-pending')}
                className={`flex cursor-pointer items-center justify-between rounded-lg border border-transparent px-3 py-3 text-left transition hover:border-slate-200 ${
                  todo.creativesPending > 0 ? 'border-l-4 border-l-amber-400 bg-amber-50/90' : 'bg-slate-50'
                }`}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-slate-900">Review Creatives</span>
                  <span className="text-xs text-slate-500">Moderation queue</span>
                </div>
                <span className="text-lg font-bold text-primary">{todo.creativesPending}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {showActivityLog && <ActivityLogModal open={showActivityLog} onClose={() => setShowActivityLog(false)} />}

      {showGoalEdit && (
        <div
          role="presentation"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={() => !goalSaving && setShowGoalEdit(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-7 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Edit User Goal</h3>
            <label className="mb-1.5 block text-xs font-medium text-slate-500">Target Users</label>
            <input
              type="number"
              min="1"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = parseInt(goalInput, 10);
                  if (!goalSaving && !isNaN(val) && val > 0) persistUserGoal(val);
                }
                if (e.key === 'Escape' && !goalSaving) setShowGoalEdit(false);
              }}
              autoFocus
              className="f-input mb-5 w-full"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="f-button f-button-secondary"
                disabled={goalSaving}
                onClick={() => !goalSaving && setShowGoalEdit(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="f-button"
                disabled={goalSaving}
                onClick={() => {
                  const val = parseInt(goalInput, 10);
                  if (!isNaN(val) && val > 0) persistUserGoal(val);
                }}
              >
                {goalSaving ? <FaSpinner className="fa-spin" /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
